/* eslint-disable array-callback-return */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useOnClickOutside from 'use-onclickoutside';
import moment from 'moment-timezone';

import AnalyticsCard from './AnalyticsCard/AnalyticsCard';
// import AnalyticsSortDropdown from './AnalyticsSortDropdown/AnalyticsSortDropdown';
import Button from 'components/Button/Button';
// import ListView from 'scenes/templateEditor/components/Icons/ListView';
// import GridView from 'scenes/templateEditor/components/Icons/GridView';
import DirectionIcon from 'components/DirectionIcon/DirectionIcon';
import DateRangePicker from 'components/DateRangePicker/DateRangePicker';
import Loading from 'components/Loading/Loading';
import Warning from 'components/Icons/Warning';
import AttributionWindowModal from './Modal/AttributionWindowModal';
import ImageModal from 'components/ImageModal/ImageModal';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import colors from 'theme/Colors.module.scss';
import './Analytics.scss';
import placeholderListViewImg from 'static/images/placeholder-list-view.png';

import {
  Box,
  Button as regularButton,
  Grid,
  GridItem,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Text,
  TableContainer,
  Progress,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { defaultDates, isValidDate } from 'helpers/presetDateHelpers';
import { paramsToString, paramsToObject } from 'helpers/paramHelpers';
import { formatAOV, formatPercentage } from 'helpers/campaignHelper';
import { exportToCSV } from 'helpers/csvHelpers';
import { checkCampaignStatus, getTemplateThumb } from 'helpers/campaignHelper';
import { awPresetOptions } from 'helpers/presetDateHelpers';

import OutsideClickHandler from 'hooks/OutsideClickHandler';

import { useGetStatsQuery } from 'modules/dashboard/dashboardApi';
import { useGetTemplatesQuery } from 'modules/templates/templatesApi';
import { useGetCampaignsQuery } from 'modules/campaigns/campaignsApi';
import { setDateRange } from 'modules/app/appSlice';
import { setAnalyticsParamsToState } from 'modules/analytics/analyticsSlice';

export const campaignFilterOptions = [
  {
    label: 'Personalized Packages: High to Low',
    type: 'packages',
    order: 'desc',
  },
  {
    label: 'Personalized Packages: Low to High',
    type: 'packages',
    order: 'asc',
  },
  {
    label: 'Conversions: High to Low',
    type: 'conversions',
    order: 'desc',
  },
  {
    label: 'Conversions: Low to High',
    type: 'conversions',
    order: 'asc',
  },
  {
    label: 'Conversion Rate: High to Low',
    type: 'conversion_rate',
    order: 'desc',
  },
  {
    label: 'Conversion Rate: Low to High',
    type: 'conversion_rate',
    order: 'asc',
  },
  { label: 'Conversion AOV: High to Low', type: 'avg', order: 'desc' },
  { label: 'Conversion AOV: Low to High', type: 'avg', order: 'asc' },
  { label: 'Total QR Scans: High to Low', type: 'qrScans', order: 'desc' },
  { label: 'Total QR Scans: Low to High', type: 'qrScans', order: 'asc' },
];

const Analytics = props => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const rangePickerRef = useRef(null);
  const selectCampaignSortRef = useRef(null);

  const [dateRangeDropdownVisible, setDateRangeDropdownVisible] =
    useState(false);
  const [isCustomRangePickerVisible, setIsCustomRangePickerVisible] =
    useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isListView, setIsListView] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isOrderOpen, setIsOrderOpen] = useState(false);

  const [filteredCampaigns, setFilteredCampaigns] = useState({
    campaigns: [],
    foots: [],
    sort: [{ field: 'packages', direction: 'desc' }],
  });
  // eslint-disable-next-line no-unused-vars
  const [filterOption, setFilterOption] = useState(campaignFilterOptions[0]);

  const [selectedRowId, setSelectedRowId] = useState(null);

  const {
    isOpen: isAwModalOpen,
    // eslint-disable-next-line no-unused-vars
    onOpen: onAwModalOpen,
    onClose: onAwModalClose,
  } = useDisclosure();
  const {
    isOpen: isImageModalOpen,
    onOpen: onImageModalOpen,
    onClose: onImageModalClose,
  } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(null);

  // Selectors
  const analyticsParamsFromState = useSelector(
    state => state.analytics.analyticsParamsFromState
  ); // Access stored dashboard data
  const dateRange = useSelector(state => state.app.dateRange); // Access stored user data

  useOnClickOutside(selectCampaignSortRef, e => {
    if (
      e.target.className.length > 0 &&
      e.target.className.includes('analytics-sort-dropdown-wrapper')
    )
      return;
    setIsTypeOpen(false);
    setIsOrderOpen(false);
  });

  useEffect(() => {
    setIsTypeOpen(false);
    setIsOrderOpen(false);
  }, [filterOption]);

  // Dates Calculation
  const {
    start: userStart,
    end: userEnd,
    aw,
  } = paramsToObject(analyticsParamsFromState || search);

  const { from, to } = dateRange;

  const userAw =
    aw != null &&
    awPresetOptions.find(
      awPresetOption => Number(awPresetOption.days) === Number(aw)
    )
      ? aw
      : 90;

  const range = {
    start: moment(from).local().format('YYYY-MM-DD'),
    startUtc: moment(from).startOf('day').utc().format(),
    startRaw: moment(from).local(),
    end: moment(to).local().format('YYYY-MM-DD'),
    endUtc: moment(to).endOf('day').utc().format(),
    endRaw: moment(to).local(),
    days: moment(to).diff(moment(from), 'days'),
  };

  const getInitialDateState = () => {
    return {
      from: userStart
        ? new Date(moment(userStart))
        : new Date(defaultDates.start),
      to: userEnd ? new Date(moment(userEnd)) : new Date(defaultDates.end),
      aw: userAw ? userAw : 90,
    };
  };

  const [attributionWindow, setAttributionWindow] = useState(userAw);

  const params = paramsToString({
    start: range.start,
    end: range.end,
    aw: attributionWindow,
  });

  const utcParams = paramsToString({
    start: range.startUtc,
    end: range.endUtc,
    tz: moment.tz(moment.tz.guess()).zoneAbbr(),
    all_campaigns: 1,
    show_by_day: 0,
    convdays: attributionWindow,
  });

  const {
    data: campaignsData,
    isLoading: campaignsDataIsLoading,
    isFetching: campaignsDataIsFetching,
    error: campaignsDataError,
  } = useGetCampaignsQuery({
    current_page: 1,
    size: 10000,
  });

  const {
    data: templatesData,
    isLoading: templatesDataIsLoading,
    isFetching: templatesDataIsFetching,
    error: templatesDataError,
  } = useGetTemplatesQuery({
    current_page: 1,
    size: 10000,
  });

  const {
    data: statesData,
    isLoading: statesDataIsLoading,
    isFetching: statesDataIsFetching,
    error: statesDataError,
  } = useGetStatsQuery(utcParams, {
    skip:
      !isValidDate(moment(from)) ||
      !isValidDate(moment(to)) ||
      dateRange?.length === 0,
  });

  // Initialization
  useEffect(() => {
    if (dateRange?.length === 0) {
      dispatch(setDateRange(getInitialDateState()));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      isValidDate(moment(from)) &&
      isValidDate(moment(to)) &&
      dateRange?.length !== 0
    ) {
      if (
        !inAttributionWindow() &&
        window.sessionStorage.getItem('aw-warning') !== 'false'
      ) {
        onAwModalOpen();
      }
      dispatch(setAnalyticsParamsToState(params));
      navigate(`/analytics${params}`);
    } else {
      dispatch(setDateRange(getInitialDateState()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, attributionWindow]);

  const inAttributionWindow = () => {
    // attribution window warning logic
    // x = end of date range
    // y = attribution window in days
    // z = datetime now
    // (x + y) > z  == true then show show the warning message
    const now = moment().startOf('day').utc().format('YYYY-MM-DD');
    const endWithAttr = moment(to)
      .add(attributionWindow, 'd')
      .endOf('day')
      .utc()
      .format('YYYY-MM-DD');
    const diff = moment(endWithAttr).diff(now, 'days');
    return diff > 0 ? false : true;
  };

  const columns = [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Campaign',
      textAlign: 'center',
      enableSorting: false,
      enableResizing: true,
      size: 400,
      minSize: 200,
      cell: props => {
        const campaignData = props.row.original;
        const imageUrl = campaignData.template || placeholderListViewImg;
        return (
          <Box className="analytics-name__container">
            <Box className="analytics-name__img-wrapper">
              <Box
                className="analytics-name__img"
                bgImage={imageUrl}
                onClick={() => {
                  setSelectedImage(imageUrl);
                  onImageModalOpen();
                }}
              />
            </Box>
            <Text
              className="analytics-name__text ellipsis-span"
              onClick={() => {
                navigate(`/campaigns/${campaignData.id}`, {
                  state: { from: 'analytics' },
                });
              }}
            >
              {props.getValue() || '--'}
            </Text>
          </Box>
        );
      },
      footer: () => 'Total for all campaigns',
    },
    {
      id: 'run_dates',
      accessorKey: '',
      header: 'Run Dates',
      textAlign: 'center',
      enableSorting: false,
      enableResizing: false,
      width: '160px',
      cell: ({ row }) => {
        const campaignData = row.original;
        return (
          <Text>{`${campaignData.start_date || '--/--/--'} - ${
            campaignData.end_date || '--/--/--'
          }`}</Text>
        );
      },
    },
    {
      id: 'packages',
      accessorKey: 'packages',
      header: 'Personalized Packages',
      textAlign: 'right',
      enableResizing: false,
      width: '140px',
      cell: props => <Text>{Number(props.getValue()).toLocaleString()}</Text>,
      footer: () => {
        const matchedFoot = filteredCampaigns.foots.find(
          foot => foot.field === 'packages'
        );
        return <>{Number(matchedFoot?.value).toLocaleString()}</>;
      },
    },
    {
      id: 'qrScans',
      accessorKey: 'qrScans',
      header: 'Lifetime QR Scans',
      textAlign: 'right',
      enableResizing: false,
      width: '160px',
      cell: props => {
        const campaignData = props.row.original;
        return (
          <Box className="analytics-qrc">
            {props.getValue() === 0 && props.getValue()}
            {props.getValue() > 0 && (
              <Menu>
                <MenuButton
                  className="qrc__menu-btn"
                  onClick={e => e.stopPropagation()}
                  as={regularButton}
                  rightIcon={<ChevronDownIcon />}
                >
                  {Number(props.getValue()).toLocaleString()}
                </MenuButton>
                <MenuList className="qrc__menu-list">
                  {campaignData.qrIx.map((item, index) => (
                    <MenuItem
                      className="qrc__menu-item"
                      key={index}
                      onClick={e => {
                        e.stopPropagation();
                        window.open(item.get('qr')[1], '_blank');
                      }}
                    >
                      <Tooltip
                        bg="linear-gradient(0deg, rgba(241, 95, 36, 0.20) 0%, rgba(241, 95, 36, 0.20) 100%), #FFF"
                        label={<Text color="#F15F24">{item.get('qr')[1]}</Text>}
                        aria-label="A tooltip"
                      >
                        <Box>
                          <Text as="u" fontSize={'12px'} fontWeight={400}>
                            {item.get('qr')[0]}
                          </Text>
                          <Text fontSize={'12px'} fontWeight={600}>
                            {item.get('amount')}
                          </Text>
                        </Box>
                      </Tooltip>
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
          </Box>
        );
      },
      footer: () => {
        const matchedFoot = filteredCampaigns.foots.find(
          foot => foot.field === 'qrScans'
        );
        return <>{Number(matchedFoot?.value).toLocaleString()}</>;
      },
    },
    {
      id: 'conversions',
      accessorKey: 'conversions',
      header: 'Conversions',
      enableResizing: false,
      textAlign: 'right',
      width: '140px',
      cell: props => <Text>{Number(props.getValue()).toLocaleString()}</Text>,
      footer: () => {
        const matchedFoot = filteredCampaigns.foots.find(
          foot => foot.field === 'conversions'
        );
        return <>{Number(matchedFoot?.value).toLocaleString()}</>;
      },
    },
    {
      id: 'conversion_rate',
      accessorKey: 'conversion_rate',
      header: 'Conversion Rate',
      enableResizing: false,
      textAlign: 'right',
      width: '140px',
      cell: props => <Text>{formatPercentage(props.getValue())}%</Text>,
      footer: () => {
        const matchedFoot = filteredCampaigns.foots.find(
          foot => foot.field === 'conversion_rate'
        );
        return <>{`${formatPercentage(matchedFoot?.value)}%`}</>;
      },
    },
    {
      id: 'avg',
      accessorKey: 'avg',
      header: 'Conversion AOV',
      enableResizing: false,
      textAlign: 'right',
      width: '140px',
      cell: props => <Text>{formatAOV(props.getValue())}</Text>,
      footer: () => {
        const matchedFoot = filteredCampaigns.foots.find(
          foot => foot.field === 'avg'
        );
        return <>{`${formatAOV(matchedFoot?.value)}`}</>;
      },
    },
    // temporary hidden untill the data is available
    // {
    //   accessorKey: 'avg_days_to_next_order',
    //   header: 'Avg Days to Next Order',
    //   textAlign: 'right',
    //   cell: props => <Text>{props.getValue() || '--'}</Text>,
    // },
    // {
    //   accessorKey: 'revenue',
    //   header: 'Revenue',
    //   textAlign: 'right',
    //   cell: props => <Text>{props.getValue() || '--'}</Text>,
    // },
    // {
    //   accessorKey: 'revenue_package',
    //   header: 'Revenue/Package',
    //   textAlign: 'right',
    //   cell: props => <Text>{props.getValue() || '--'}</Text>,
    // },
  ];

  useEffect(() => {
    let newCampaigns = [];
    statesData &&
      campaignsData &&
      // qrCodesStats &&
      templatesData &&
      // eslint-disable-next-line array-callback-return
      Object.values(statesData.data).map(stat => {
        let object = {};
        object.id = stat.id;
        object.conversions = stat.total_conv;
        object.packages = stat.total_imp;
        object.conversion_rate = stat.total_conv_pct;
        object.avg = stat.data[0][6];
        let temp = campaignsData.data.entities.filter(
          campaign => campaign.id === stat.id
        );
        object.enabled = temp[0]?.enabled;
        let template = templatesData?.data.entities.filter(
          template => template.id === temp[0]?.ad_template_id
        );
        object.template = getTemplateThumb(template[0]);
        object.name = temp[0]?.name;
        object.start_date = temp[0]?.start_date
          ? moment(temp[0]?.start_date).format('MM/DD/YY')
          : undefined;
        object.end_date = temp[0]?.end_date
          ? moment(temp[0]?.end_date).format('MM/DD/YY')
          : undefined;
        object.qrScans = stat.total_scans;
        object.qrIx = [];
        stat.qr_ix?.map((item, index) => {
          if (stat.data[0][7].length === 0) return;
          object.qrIx[index] = new Map();
          object.qrIx[index].set('qr', item);
          object.qrIx[index].set('amount', stat.data[0][7][index]);
        });
        newCampaigns.push(object);
      });
    let fieldName = filterOption.type;
    let isAscending = filterOption.order === 'asc';
    let sortedCampaigns = newCampaigns.sort((campaign1, campaign2) => {
      if (isAscending) return campaign1[fieldName] - campaign2[fieldName];
      else return campaign2[fieldName] - campaign1[fieldName];
    });

    // generate total data for foot section
    let footData = [];

    columns.forEach(header => {
      let data = {
        name: header.header,
        field: header.id,
        value: undefined,
      };

      if (header.id === 'name') {
        data.name = 'Total for all campaigns';
      } else {
        sortedCampaigns.forEach(campaign => {
          if (header.id === 'run_dates') {
            data.value = '';
          } else if (header.id !== 'name' && header.id !== 'run_dates') {
            if (typeof campaign[header.id] === 'number') {
              data.value === undefined
                ? (data.value = campaign[header.id])
                : (data.value += campaign[header.id]);
            } else if (typeof campaign[header.id] === 'string') {
              data.value === undefined
                ? (data.value = campaign[header.id])
                : (data.value = (
                    +data.value + +campaign[header.id]
                  ).toString());
            }
          }
        });
      }

      /* customize data display in total section */

      // calculate average conversion rate
      if (header.id === 'conversion_rate') {
        if (footData[2].value === 0) {
          data.value = 0;
        } else {
          data.value = footData[4].value / footData[2].value;
        }
      }

      // calculate average conversion AOV
      if (header.id === 'avg') {
        data.value = (+data.value / sortedCampaigns.length).toString();
      }

      footData.push(data);
    });

    setFilteredCampaigns(prev => ({
      ...prev,
      campaigns: sortedCampaigns,
      foots: footData,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statesData,
    // qrCodesStats,
    campaignsData,
    templatesData,
    filterOption.type,
    filterOption.order,
  ]);

  const table = useReactTable({
    data: filteredCampaigns.campaigns || [],
    columns,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Handle rows per clicked
  const handleRowClick = row => {
    setSelectedRowId(prev => (prev === row.id ? null : row.id)); // Toggle selection
  };

  // Handle sort clicked
  const handleSortClick = header => {
    if (header.column.getNextSortingOrder() === 'asc') {
      header.column.toggleSorting(false); // Set to 'asc'
      setFilteredCampaigns({
        ...filteredCampaigns,
        sort: [
          {
            field: header.id,
            direction: 'asc',
          },
        ],
      });
    } else if (header.column.getNextSortingOrder() === 'desc') {
      header.column.toggleSorting(true); // Set to 'desc'
      setFilteredCampaigns({
        ...filteredCampaigns,
        sort: [
          {
            field: header.id,
            direction: 'desc',
          },
        ],
      });
    } else {
      header.column.clearSorting(); // Remove sorting
      setFilteredCampaigns({
        ...filteredCampaigns,
        sort: [],
      });
    }
  };

  const handleExport = () => {
    const csvList = [];
    const csvHead = [...columns.map(header => header.header)];
    let csvData = '';

    filteredCampaigns.campaigns.forEach(campaign => {
      const campaignData = [];
      columns.forEach(header => {
        if (header.id === 'run_dates') {
          campaignData.push(
            `${campaign.start_date ? campaign.start_date : '--/--/--'} - ${
              campaign.end_date ? campaign.end_date : '--/--/--'
            }`
          );
        } else if (header.id === 'conversion_rate') {
          campaignData.push(`${formatPercentage(campaign[header.id])}%`);
        } else if (header.id === 'avg') {
          campaignData.push(`${formatAOV(campaign[header.id])}`);
        } else {
          campaignData.push(campaign[header.id]);
        }
      });
      csvData += campaignData.join(',') + '\n';
    });
    csvList.push(csvHead + '\n');
    csvData += '\n'; // add line break to seperate data row from total row

    const footData = [];
    filteredCampaigns.foots.forEach(foot => {
      if (foot.field === 'name') {
        footData.push(foot.name);
      } else if (foot.field === 'run_dates') {
        footData.push('');
      } else if (foot.field === 'conversion_rate') {
        footData.push(`"${formatPercentage(foot.value)}%"`);
      } else if (foot.field === 'avg') {
        footData.push(`"${formatAOV(foot.value)}"`);
      } else {
        footData.push(foot.value);
      }
    });

    csvData += footData.join(',') + '\n';
    csvList.push(csvData);

    exportToCSV(csvList, `analytics_${range.start}-${range.end}`);
  };

  if (campaignsDataError) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch campaigns data',
      })
    );
  }

  if (templatesDataError) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch templates data',
      })
    );
  }

  if (statesDataError) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch states data',
      })
    );
  }

  if (campaignsDataIsLoading || templatesDataIsLoading || statesDataIsLoading) {
    return <Loading type="fullpage" />;
  }

  const buildGridView = () => {
    const campaigns = filteredCampaigns.campaigns;
    return (
      <>
        <Box className="analytics-grid-view__container">
          <Grid className="analytics-grid__wrapper" background={colors.white}>
            {campaigns &&
              campaigns.map((campaign, index) => {
                return (
                  <GridItem key={`analytic-card-${index}`}>
                    <AnalyticsCard
                      startDate={
                        campaign.start_date
                          ? moment(campaign.start_date).format('MM/DD/YY')
                          : undefined
                      }
                      endDate={
                        campaign.end_date
                          ? moment(campaign.end_date).format('MM/DD/YY')
                          : undefined
                      }
                      to={campaign.id}
                      template={campaign.template}
                      conversionRate={campaign.conversion_rate}
                      campaignName={campaign.name}
                      isActive={checkCampaignStatus(campaign)}
                      packages={campaign.packages}
                      conversions={campaign.conversions}
                      average={campaign.avg}
                      qrScans={campaign.qrScans}
                      qrIx={campaign.qrIx}
                    />
                  </GridItem>
                );
              })}
          </Grid>
        </Box>
        {campaigns.length === 0 && !statesDataIsLoading && (
          <Text as="span" marginLeft={{ base: '1.25em', lg: '4em' }}>
            No data is available.
          </Text>
        )}
      </>
    );
  };
  return (
    <>
      <AttributionWindowModal
        isModalOpen={isAwModalOpen}
        onModalClose={onAwModalClose}
      />
      <ImageModal
        isModalOpen={isImageModalOpen}
        onModalClose={onImageModalClose}
        imageURL={selectedImage}
      />
      <Box className="analytics">
        <Box className="analytics__header">
          <Box className="header__title">
            <span>Unboxing Marketing Analytics</span>
          </Box>

          <Box className="header__export-btn-mobile">
            <Button
              type="button"
              icon={'export'}
              text={'Export'}
              iconColor={colors.white}
              textColor={colors.white}
              bgColor={colors.secondary900}
              hoverColor={colors.secondary700}
              textHoverColor={colors.white}
              border={`1px solid ${colors.neutral200}`}
              boxShadow={`0px 1px 2px 0px #1018280A`}
              iconHeight={11.2}
              iconWidth={9.8}
              height={32}
              width={84}
              btnStyle={'solid'}
              fontSize={12}
              fontWeight={500}
              onClick={handleExport}
            ></Button>
          </Box>

          <Box className="header__date-range-picker">
            <OutsideClickHandler
              onHandler={() => {
                setDateRangeDropdownVisible(false);
                setIsCustomRangePickerVisible(false);
              }}
              style={{ position: 'relative', right: '0px' }}
            >
              <DateRangePicker
                isCustomRangePickerVisible={isCustomRangePickerVisible}
                setIsCustomRangePickerVisible={setIsCustomRangePickerVisible}
                dateRangeDropdownVisible={dateRangeDropdownVisible}
                setDateRangeDropdownVisible={setDateRangeDropdownVisible}
                attributionWindow={attributionWindow}
                setAttributionWindow={setAttributionWindow}
                date={{ from, to }}
                ref={rangePickerRef}
              />
            </OutsideClickHandler>
          </Box>
          <Box className="header__export-btn">
            <Button
              type="button"
              icon={'export'}
              text={'Export'}
              iconColor={colors.white}
              textColor={colors.white}
              bgColor={colors.secondary900}
              hoverColor={colors.secondary700}
              textHoverColor={colors.white}
              border={`1px solid ${colors.neutral200}`}
              boxShadow={`0px 1px 2px 0px #1018280A`}
              iconHeight={11.2}
              iconWidth={9.8}
              height={32}
              width={84}
              btnStyle={'solid'}
              fontSize={12}
              fontWeight={500}
              onClick={handleExport}
            ></Button>
          </Box>
        </Box>

        <Box className="analytics-body">
          {!inAttributionWindow() && (
            <Box className="table__header">
              <Box className="header__notification">
                <Warning
                  height="16px"
                  width="16px"
                  style={{ backgroundColor: colors.primary900 }}
                />
                <span>
                  {`The attribution window you have selected is not long enough.
                  Please select an end date before ${moment()
                    .subtract(attributionWindow, 'd')
                    .startOf('day')
                    .utc()
                    .format('MMMM D, YYYY')} for more accurate results.`}
                </span>
              </Box>
              {/* comment out grid view for futher notice */}
              {/* <Box className="header__view-btn">
            {!isListView && (
              <Box
                className="header__gridview-sort-dropdown"
                ref={selectCampaignSortRef}
                color={colors.neutral700}
              >
                <AnalyticsSortDropdown
                  isTypeOpen={isTypeOpen}
                  setIsTypeOpen={setIsTypeOpen}
                  isOrderOpen={isOrderOpen}
                  setIsOrderOpen={setIsOrderOpen}
                  campaignFilterOptions={campaignFilterOptions}
                  setFilterOption={setFilterOption}
                />
              </Box>
            )}
            <IconButton
              className="view-switch__btn"
              size="sm"
              background={isListView ? colors.coolGrey1 : colors.white}
              onClick={() => setIsListView(true)}
              icon={<ListView size="0.75em" />}
            />
            <IconButton
              className="view-switch__btn"
              background={!isListView ? colors.coolGrey1 : colors.white}
              size="sm"
              onClick={() => setIsListView(false)}
              icon={<GridView size="0.75em" />}
            />
          </Box> */}
            </Box>
          )}
          {isListView && (
            <Box className="analytics-table">
              <TableContainer>
                {(campaignsDataIsFetching ||
                  templatesDataIsFetching ||
                  statesDataIsFetching) && (
                  <Progress size="xs" isIndeterminate />
                )}
                <Box
                  className={`analytics-table__wrapper ${
                    !inAttributionWindow() ? 'table-warning' : ''
                  }`}
                >
                  <Table
                    size="md"
                    variant="simple"
                    style={{
                      borderCollapse: 'separate',
                      borderSpacing: 0,
                    }}
                  >
                    <Thead zIndex={3} bg="white">
                      {table.getHeaderGroups().map(headerGroup => (
                        <Tr
                          className="analytics-table__row"
                          key={headerGroup.id}
                        >
                          {headerGroup.headers.map((header, index) => {
                            const textAlign =
                              header.column.columnDef.textAlign || 'left';
                            const width = header.column.columnDef.width || null;
                            const isResizable = header.column.getCanResize();

                            return (
                              <Th
                                className="analytics-table__header"
                                key={header.id}
                                position="sticky"
                                top={0}
                                left={index === 0 ? 0 : undefined}
                                zIndex={index === 0 ? 3 : 2}
                                whiteSpace="nowrap"
                                textAlign={textAlign}
                                width={isResizable ? header.getSize() : width}
                                minWidth={header.column.columnDef.minSize}
                                maxWidth={header.column.columnDef.maxSize}
                                userSelect="none"
                                bg={
                                  filteredCampaigns?.sort[0]?.field ===
                                  header.id
                                    ? colors.primary50
                                    : 'white'
                                }
                                textColor={
                                  filteredCampaigns?.sort[0]?.field ===
                                  header.id
                                    ? colors.primary900
                                    : colors.neutral700
                                }
                                boxShadow={
                                  '0px 2px 6px 0px rgba(16, 24, 40, 0.06)'
                                }
                              >
                                {header.column.getCanSort() ? (
                                  <Flex justify={textAlign} align="center">
                                    <Text
                                      className="analytics-table__header-text"
                                      mr={2}
                                      onClick={() => handleSortClick(header)}
                                      cursor={
                                        header.column.getCanSort()
                                          ? 'pointer'
                                          : 'default'
                                      }
                                    >
                                      {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                          )}
                                    </Text>

                                    <Box
                                      onClick={() => handleSortClick(header)}
                                      cursor={
                                        header.column.getCanSort()
                                          ? 'pointer'
                                          : 'default'
                                      }
                                    >
                                      {filteredCampaigns?.sort[0]?.field ===
                                      header.id ? (
                                        <DirectionIcon
                                          type={'alpha'}
                                          direction={
                                            filteredCampaigns?.sort[0]
                                              ?.direction || 'asc'
                                          }
                                          style={{
                                            color:
                                              filteredCampaigns?.sort[0]
                                                ?.field === header.id
                                                ? colors.primary900
                                                : colors.neutral300,
                                          }}
                                        />
                                      ) : (
                                        <DirectionIcon
                                          type={'alpha'}
                                          direction={'normal'}
                                          style={{
                                            color:
                                              filteredCampaigns?.sort[0]
                                                ?.field === header.id
                                                ? colors.primary900
                                                : colors.neutral300,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Flex>
                                ) : (
                                  <Text
                                    className="analytics-table__header-text"
                                    mr={2}
                                  >
                                    {header.isPlaceholder
                                      ? null
                                      : flexRender(
                                          header.column.columnDef.header,
                                          header.getContext()
                                        )}
                                  </Text>
                                )}

                                {/* Resize handle (shown only if resizable) */}
                                {isResizable && (
                                  <Box
                                    onMouseDown={header.getResizeHandler()}
                                    onTouchStart={header.getResizeHandler()}
                                    position="absolute"
                                    right={0}
                                    top={0}
                                    height="100%"
                                    width="4px"
                                    cursor="col-resize"
                                    zIndex={1}
                                    _hover={{ bg: colors.neutral200 }}
                                  />
                                )}
                              </Th>
                            );
                          })}
                        </Tr>
                      ))}
                    </Thead>
                    <Tbody>
                      {table.getRowModel().rows.map((row, index) => {
                        const isLastRow =
                          index === table.getRowModel().rows.length - 1;
                        return (
                          <Tr
                            key={row.id}
                            onClick={() => handleRowClick(row)}
                            bg={
                              row.id === selectedRowId
                                ? 'gray.100'
                                : 'transparent'
                            }
                            _hover={{ bg: colors.neutral200 }}
                            cursor="pointer"
                          >
                            {row.getVisibleCells().map((cell, index) => {
                              const textAlign =
                                cell.column.columnDef.textAlign || 'left';
                              const width = cell.column.columnDef.width || null;
                              const isResizable =
                                cell.column.columnDef.enableResizing;
                              const headerId = cell.column.columnDef.id;

                              return (
                                <Td
                                  className="analytics-table__body-cell"
                                  px={index === 0 ? '0px' : '15px'}
                                  py="0px"
                                  key={cell.id}
                                  position={index === 0 ? 'sticky' : 'static'}
                                  left={index === 0 ? 0 : undefined}
                                  zIndex={index === 0 ? 1 : undefined}
                                  textAlign={textAlign}
                                  width={
                                    isResizable ? cell.column.getSize() : width
                                  }
                                  bg={
                                    filteredCampaigns?.sort[0]?.field ===
                                    headerId
                                      ? colors.primary50
                                      : index === 0
                                      ? 'white'
                                      : 'unset'
                                  }
                                  borderBottom={
                                    isLastRow
                                      ? 'none'
                                      : `1px solid ${colors.neutral200}`
                                  }
                                  textColor={
                                    filteredCampaigns?.sort[0]?.field ===
                                    headerId
                                      ? colors.primary900
                                      : colors.neutral700
                                  }
                                  fontWeight={
                                    filteredCampaigns?.sort[0]?.field ===
                                    headerId
                                      ? 600
                                      : 'unset'
                                  }
                                  boxShadow={
                                    index === 0
                                      ? '2px 0 5px rgba(16, 24, 40, 0.06)'
                                      : undefined
                                  }
                                >
                                  {index === 0 ? (
                                    <Box className="analytics-table__first-cell-container">
                                      <Box
                                        className="analytics-table__indicator"
                                        bg={`${
                                          row.original.enabled
                                            ? colors.status00
                                            : colors.neutral200
                                        }`}
                                      ></Box>
                                      <Box className="analytics-table__first-cell">
                                        {flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext()
                                        )}
                                      </Box>
                                    </Box>
                                  ) : (
                                    flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )
                                  )}
                                </Td>
                              );
                            })}
                          </Tr>
                        );
                      })}
                    </Tbody>

                    <Tfoot>
                      <Tr>
                        {table
                          .getFooterGroups()[0]
                          .headers.map((footer, index) => {
                            const textAlign =
                              footer.column.columnDef.textAlign || 'left';

                            return (
                              <Th
                                className="analytics-table__footer-header"
                                key={footer.id}
                                position="sticky"
                                bottom={0}
                                left={index === 0 ? 0 : undefined}
                                zIndex={index === 0 ? 3 : 2}
                                bg={colors.neutral50}
                                textAlign={textAlign}
                              >
                                {flexRender(
                                  footer.column.columnDef.footer,
                                  footer.getContext()
                                )}
                              </Th>
                            );
                          })}
                      </Tr>
                    </Tfoot>
                  </Table>
                </Box>
              </TableContainer>
            </Box>
          )}
          {!isListView && buildGridView()}
        </Box>
      </Box>
    </>
  );
};

export default Analytics;
